import type { BookingVehicle } from '@/types'

import { SERVICE_TYPES } from '@/constants/site'

/*
 * Booking request rules shared by the form (instant feedback) and the API
 * route (the real check). Safe to import in the browser.
 */

export interface BookingInput {
    servicio: string
    fecha: string
    hora: string
    recogida: string
    destino: string
    pasajeros: number
    vehiculo_id: string
    nombre: string
    email: string
    telefono: string
    comentarios: string
    // Honeypot: hidden from people, filled in by bots.
    empresa_web: string
}

export type BookingField = Exclude<keyof BookingInput, 'empresa_web'>

export type BookingErrors = Partial<Record<BookingField, string>>

export const BOOKING_LIMITS = {
    recogida: 200,
    destino: 200,
    nombre: 80,
    email: 160,
    telefono: 25,
    comentarios: 1500,
    pasajeros: 60
} as const

export const BOOKING_FIELD_ORDER: Array<BookingField> = [
    'servicio',
    'vehiculo_id',
    'fecha',
    'hora',
    'recogida',
    'destino',
    'pasajeros',
    'nombre',
    'telefono',
    'email',
    'comentarios'
]

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/
const PHONE_PATTERN = /^\+?[\d\s().-]{6,25}$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/
const LINE_BREAK = /[\r\n]/

export const createEmptyBooking = (vehicleId = ''): BookingInput => ({
    servicio: '',
    fecha: '',
    hora: '',
    recogida: '',
    destino: '',
    pasajeros: 1,
    vehiculo_id: vehicleId,
    nombre: '',
    email: '',
    telefono: '',
    comentarios: '',
    empresa_web: ''
})

// YYYY-MM-DD in the local time zone of whoever runs it (the visitor's browser).
export const toDateKey = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${date.getFullYear()}-${month}-${day}`
}

// YYYY-MM-DD in UTC, for the server.
export const toUtcDateKey = (date: Date): string => date.toISOString().slice(0, 10)

const isRealDate = (value: string): boolean => {
    if (!DATE_PATTERN.test(value)) return false

    const [year, month, day] = value.split('-').map(Number)
    const date = new Date(Date.UTC(year, month - 1, day))

    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

const addYears = (dateKey: string, years: number): string => `${Number(dateKey.slice(0, 4)) + years}${dateKey.slice(4)}`

// Coerces any request body into the expected shape, with every text trimmed.
export const normalizeBooking = (raw: unknown): BookingInput => {
    const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>

    const text = (key: keyof BookingInput): string => {
        const value = source[key]

        return typeof value === 'string' ? value.trim() : ''
    }

    const passengers = Number(source.pasajeros)

    return {
        servicio: text('servicio'),
        fecha: text('fecha'),
        hora: text('hora'),
        recogida: text('recogida'),
        destino: text('destino'),
        pasajeros: Number.isFinite(passengers) ? Math.trunc(passengers) : 0,
        vehiculo_id: text('vehiculo_id'),
        nombre: text('nombre'),
        email: text('email'),
        telefono: text('telefono'),
        comentarios: text('comentarios'),
        empresa_web: text('empresa_web')
    }
}

export const validateBooking = (input: BookingInput, today: string, vehicle?: BookingVehicle | null): BookingErrors => {
    const errors: BookingErrors = {}
    const singleLine = (value: string, max: number) => value.length <= max && !LINE_BREAK.test(value)

    if (!(SERVICE_TYPES as ReadonlyArray<string>).includes(input.servicio)) errors.servicio = 'Elige el tipo de servicio.'

    if (input.vehiculo_id.length > 64) errors.vehiculo_id = 'Elige un vehículo de la lista.'

    if (!input.fecha) errors.fecha = 'Indica la fecha del servicio.'
    else if (!isRealDate(input.fecha)) errors.fecha = 'Escribe una fecha válida.'
    else if (input.fecha < today) errors.fecha = 'La fecha no puede ser anterior a hoy.'
    else if (input.fecha > addYears(today, 2)) errors.fecha = 'Elige una fecha dentro de los próximos dos años.'

    if (!input.hora) errors.hora = 'Indica la hora de recogida.'
    else if (!TIME_PATTERN.test(input.hora)) errors.hora = 'Escribe una hora válida, por ejemplo 18:30.'

    if (input.recogida.length < 3) errors.recogida = 'Indica la dirección o el lugar de recogida.'
    else if (!singleLine(input.recogida, BOOKING_LIMITS.recogida)) errors.recogida = `Usa una sola línea de ${BOOKING_LIMITS.recogida} caracteres como máximo.`

    if (!singleLine(input.destino, BOOKING_LIMITS.destino)) errors.destino = `Usa una sola línea de ${BOOKING_LIMITS.destino} caracteres como máximo.`

    if (!Number.isInteger(input.pasajeros) || input.pasajeros < 1 || input.pasajeros > BOOKING_LIMITS.pasajeros) {
        errors.pasajeros = `Indica entre 1 y ${BOOKING_LIMITS.pasajeros} pasajeros.`
    }
    else if (vehicle && input.pasajeros > vehicle.pasajeros) {
        errors.pasajeros = `Este vehículo admite hasta ${vehicle.pasajeros} ${vehicle.pasajeros === 1 ? 'pasajero' : 'pasajeros'}. Elige otro o déjalo sin preferencia.`
    }

    if (input.nombre.length < 2) errors.nombre = 'Escribe tu nombre.'
    else if (!singleLine(input.nombre, BOOKING_LIMITS.nombre)) errors.nombre = `Usa ${BOOKING_LIMITS.nombre} caracteres como máximo.`

    if (input.email.length > BOOKING_LIMITS.email || !EMAIL_PATTERN.test(input.email)) errors.email = 'Escribe un email válido.'

    if (!PHONE_PATTERN.test(input.telefono) || input.telefono.replace(/\D/g, '').length < 6) {
        errors.telefono = 'Escribe un teléfono válido para poder confirmarte la reserva.'
    }

    if (input.comentarios.length > BOOKING_LIMITS.comentarios) errors.comentarios = `Usa ${BOOKING_LIMITS.comentarios} caracteres como máximo.`

    return errors
}
